import { adminNavigationSections } from './adminNavigationConfig';
import { ROUTE_ACCESS_RULES } from './adminAccessControl';

const STATIC_ROUTE_DEFINITIONS = [
  { label: 'PT Setting', path: '/fitness-transactions/setup/pt-setting', section: 'Programs' },
  { label: 'QR Module', path: '/showqr', section: 'Utilities' },
];

const normalizePath = value => {
  const normalized = (value || '').trim();
  if (!normalized) {
    return '';
  }
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const isRouteLikePath = value => {
  const normalized = normalizePath(value);
  if (!normalized) {
    return false;
  }
  if (!normalized.startsWith('/') || normalized.startsWith('//')) {
    return false;
  }
  if (normalized.includes(' ') || normalized.includes('?') || normalized.includes('#')) {
    return false;
  }
  return true;
};

const formatRouteLabel = path => {
  const normalized = normalizePath(path);
  if (!normalized) {
    return '';
  }

  return normalized
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .map(part => {
      if (part.startsWith(':')) {
        return part.slice(1).toUpperCase();
      }

      return part
        .split('-')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    })
    .join(' / ');
};

const navigationRouteCatalog = adminNavigationSections
  .flatMap(section =>
    (section.items || []).map(item => ({
      label: item.label,
      path: normalizePath(item.to),
      section: section.title,
    }))
  )
  .filter(item => item.path);

const navigationLabelMap = new Map(
  navigationRouteCatalog.map(item => [item.path, item.label])
);

const accessRuleRouteCatalog = ROUTE_ACCESS_RULES.map(rule => {
  const normalizedPrefix = normalizePath(rule.prefix);
  const normalizedAccessKey = normalizePath(rule.accessKey);

  return {
    label:
      navigationLabelMap.get(normalizedPrefix) ||
      navigationLabelMap.get(normalizedAccessKey) ||
      formatRouteLabel(normalizedPrefix),
    path: normalizedPrefix,
    section: 'Access Routes',
  };
});

const buildOutletMenuModuleCatalog = () => {
  const routeMap = new Map();

  [...navigationRouteCatalog, ...accessRuleRouteCatalog, ...STATIC_ROUTE_DEFINITIONS].forEach(
    item => {
      const path = normalizePath(item.path);
      if (!isRouteLikePath(path) || path === '/dashboardadmin') {
        return;
      }

      if (!routeMap.has(path)) {
        routeMap.set(path, {
          label: item.label || formatRouteLabel(path),
          path,
          section: item.section || 'Application Routes',
        });
      }
    }
  );

  return Array.from(routeMap.values()).sort((left, right) =>
    left.path.localeCompare(right.path)
  );
};

export const outletMenuModuleCatalog = buildOutletMenuModuleCatalog();

const outletMenuModuleMap = new Map(
  outletMenuModuleCatalog.map(item => [item.path, item])
);

export const isRegisteredOutletMenuPath = path =>
  outletMenuModuleMap.has(normalizePath(path));

export const findOutletMenuModuleByPath = path => {
  const normalized = normalizePath(path);
  if (!isRouteLikePath(normalized)) {
    return null;
  }

  return (
    outletMenuModuleMap.get(normalized) || {
      label: formatRouteLabel(normalized) || normalized,
      path: normalized,
      section: 'Custom Route',
    }
  );
};

export const filterMenuModuleRecords = modules =>
  (Array.isArray(modules) ? modules : [])
    .map(module => {
      if (!module || typeof module !== 'object') {
        return null;
      }

      const route = normalizePath(
        module.Modulelink ?? module.module_link ?? module.modulelink ?? module.path ?? ''
      );
      if (!isRouteLikePath(route)) {
        return null;
      }

      const menuModule = findOutletMenuModuleByPath(route);
      const hasRegisteredRoute = isRegisteredOutletMenuPath(route);
      const existingLabel =
        module.Modulename ?? module.modulename ?? module.module_name ?? module.label ?? '';

      return {
        ...module,
        Modulelink: route,
        module_link: route,
        Modulename: hasRegisteredRoute ? menuModule.label : existingLabel || menuModule.label,
        modulename: hasRegisteredRoute ? menuModule.label : existingLabel || menuModule.label,
        menuSection: menuModule.section,
      };
    })
    .filter(Boolean);
